import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db } from '@/firebase/firebase';
import { isFirebaseOfflineError } from '@/services/errorReportingService';
import { isMobileDevice } from '@/utils/deviceDetection';

export interface OnboardingStepData {
  id: string;
  title: string;
  content: string;
  media?: string;
  mediaUrl?: string;
  media_url?: string;
  ctaLabel?: string;
  ctaAction?: string;
  orderIndex: number;
  isActive: boolean;
  allowSkip?: boolean;
  anchorId?: string;
  show_continue_button?: boolean;
  desktop: boolean;
  mobile: boolean;
  redirectUrl?: string;
}

const ONBOARDING_COLLECTION = 'plugin_onboarding_steps';

const resolveBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  if (typeof value === 'number') return value === 1;
  return false;
};

const resolveIsActive = (raw: Record<string, unknown>) => {
  const value = raw.isActive ?? raw.is_active ?? raw.active;
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return value === undefined ? true : false;
};

const normalizeStep = (id: string, raw: Partial<OnboardingStepData> & Record<string, unknown>): OnboardingStepData => {
  const rawOrder = raw.orderIndex ?? raw.order ?? raw.order_index;
  const rawContent = raw.content ?? raw.content_html;
  const rawAnchorId = raw.anchorId ?? raw.anchor_id;
  const rawDesktop = raw.desktop;
  const rawMobile = raw.mobile;
  const rawRedirectUrl = raw.redirectUrl ?? raw.redirect_url;

  return {
    id,
    title: raw.title ?? '',
    content: typeof rawContent === 'string' ? rawContent : '',
    media: raw.media,
    mediaUrl: raw.mediaUrl,
    media_url: raw.media_url,
    ctaLabel: raw.ctaLabel,
    ctaAction: raw.ctaAction,
    orderIndex: typeof rawOrder === 'number' ? rawOrder : Number(rawOrder ?? 0),
    isActive: resolveIsActive(raw),
    allowSkip: raw.allowSkip,
    anchorId: typeof rawAnchorId === 'string' ? rawAnchorId : undefined,
    show_continue_button: (raw.show_continue_button as boolean | undefined) ?? true,
    desktop: rawDesktop !== undefined ? resolveBoolean(rawDesktop) : true,
    mobile: rawMobile !== undefined ? resolveBoolean(rawMobile) : false,
    redirectUrl: typeof rawRedirectUrl === 'string' && rawRedirectUrl ? rawRedirectUrl : undefined,
  };
};

export const useOnboardingSteps = () => {
  const [steps, setSteps] = useState<OnboardingStepData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile] = useState(() => isMobileDevice());

  useEffect(() => {
    let isMounted = true;

    const loadSteps = async () => {
      try {
        const collectionRef = collection(db, ONBOARDING_COLLECTION);
        const snapshot = await getDocs(collectionRef);
        const fetched = snapshot.docs.map((doc) =>
          normalizeStep(doc.id, doc.data() as Partial<OnboardingStepData> & Record<string, unknown>),
        );

        const ordered = fetched
          .filter((step) => step.isActive)
          .filter((step) => (isMobile ? step.mobile : step.desktop))
          .sort((a, b) => a.orderIndex - b.orderIndex);

        if (isMounted) {
          setSteps(ordered);
        }
      } catch (error) {
        if (!isFirebaseOfflineError(error)) {
          Sentry.captureException(error, {
            tags: { feature: 'onboarding' },
            extra: { context: 'loading onboarding steps' },
          });
        }
        if (isMounted) {
          setSteps([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSteps();

    return () => {
      isMounted = false;
    };
  }, [isMobile]);

  return { steps, loading, isMobile };
};
